import {
    AdType,
    ArticleSectionContentProps,
    ArticleSectionType,
    InlineSocialSubType,
} from '@sport1/types/web'
import React from 'react'
import dynamic from 'next/dynamic'
import AdSlot from '@/components/Ads/AdSlot'
import TopImage from '@/components/Article/TopImage'
import TopVideo from '@/components/Article/TopVideo'
import { GLOBAL_LAYOUT_COMPONENT_SPACE } from '@/utils/content/constants'
import ArticleHeadline from '@/components/Article/Headline'
import ArticleAbstract from '@/components/Article/Abstract'
import ArticleText from '@/components/Article/Text'
import { PinpollProps } from '@/components/Pinpoll'

const ArticleImage = dynamic(() => import('@/components/Article/ImageTeaser'), { ssr: false })
const ArticleVideo = dynamic(() => import('@/components/Article/VideoTeaser'), { ssr: false })
const ArticleInlineSocial = dynamic(() => import('@/components/Article/Inline/Social')) // do we need to render it at the beginning ?
const ArticleGalleryTeaser = dynamic(() => import('@/components/Article/GalleryTeaser'), {
    ssr: false,
})
const ArticleRelatedContextItems = dynamic(() => import('@/components/Article/RelatedContextItems')) // do we need to render it at the beginning ?
const ArticlePoll = dynamic(() => import('@/components/Article/Poll')) // do we need to render it at the beginning ?
const ArticleRelatedContent = dynamic(() => import('@/components/Article/RelatedContent'), {
    ssr: false,
})
const Link = dynamic(() => import('@/components/Article/Link')) // do we need to render it at the beginning ?
const ArticleBrandstory = dynamic(() => import('@/components/Article/Brandstory')) // do we need to render it at the beginning ?
const OddsBox = dynamic(() => import('@/components/Article/OddsBox')) // do we need to render it at the beginning ?
const AutomatedAd = dynamic(() => import('@/components/Article/AutomatedAd')) // do we need to render it at the beginning ?
const Iframe = dynamic(() => import('@/components/Iframe')) // do we need to render it at the beginning ?
const Quote = dynamic(() => import('@/components/Quote')) // do we need to render it at the beginning ?
const Table = dynamic(() => import('@/components/Table')) // do we need to render it at the beginning ?
const LinkList = dynamic(() => import('@/components/LinkList'), { ssr: false })
const Pinpoll = dynamic(() => import('@/components/Pinpoll'), { ssr: false })

const renderBaseArticleComponent = (
    component: ArticleSectionContentProps,
    index: number,
    followLinks: boolean
): JSX.Element | undefined => {
    let pageItem

    const key = `${component.type}_${component.componentKey}`
    // switch is sorted alphabetical pls look to keep it this way
    switch (component.type) {
        case ArticleSectionType.ABSTRACT:
            return (pageItem = (
                <ArticleAbstract
                    key={key}
                    content={component}
                    displayArray={['none', 'inline', 'inline']}
                />
            ))
        case ArticleSectionType.PINPOLL:
            pageItem = <Pinpoll key={key} isArticle {...(component as PinpollProps)} />
            break
        case ArticleSectionType.AD:
            const {
                ad: { adType },
            } = component
            const isDisplayAd = adType === AdType.DISPLAY
            pageItem = (
                <AdSlot
                    key={key}
                    marginX={isDisplayAd ? undefined : GLOBAL_LAYOUT_COMPONENT_SPACE}
                    {...component}
                />
            )
            break
        case ArticleSectionType.ARTICLE_GALLERY:
            pageItem = <ArticleGalleryTeaser key={key} content={component} />
            break
        case ArticleSectionType.AUTOMATED_AD:
            pageItem = <AutomatedAd key={key} content={component} />
            break
        case ArticleSectionType.BRANDSTORY:
            pageItem = <ArticleBrandstory key={key} content={component} />
            break
        case ArticleSectionType.HEADLINE:
            pageItem = <ArticleHeadline key={key} content={component} />
            break
        case ArticleSectionType.IFRAME:
            if (/spotify/.test(component?.source)) {
                pageItem = (
                    <ArticleInlineSocial
                        key={key}
                        content={{
                            ...component,
                            type: ArticleSectionType.INLINE_SOCIAL,
                            url: '',
                            fallbackImageUrl:
                                'https://reshape.sport1.de/c/t/02d3b4c1-b919-4f71-9a2e-13bd4834d959/0x0',
                            subType: InlineSocialSubType.SPOTIFY_AB,
                        }}
                    />
                )
            } else pageItem = <Iframe key={key} {...component} index={index} />
            break
        case ArticleSectionType.IMAGE:
            pageItem = <ArticleImage key={key} content={component} />
            break
        case ArticleSectionType.INLINE_SOCIAL:
            pageItem = <ArticleInlineSocial key={key} content={component} />
            break
        case ArticleSectionType.LINK:
            pageItem = <Link key={key} {...component} />
            break
        case ArticleSectionType.LINK_LIST:
            pageItem = <LinkList key={key} content={component} />
            break
        case ArticleSectionType.ODDS_BOX:
            const { matchId, competitionId } = component
            pageItem = <OddsBox key={key} {...{ matchId, competitionId }} />
            break
        case ArticleSectionType.POLL:
            pageItem = <ArticlePoll key={key} content={component} />
            break
        case ArticleSectionType.QUOTE:
            pageItem = <Quote key={key} content={component} />
            break
        case ArticleSectionType.RELATED_CONTENT:
            pageItem = <ArticleRelatedContent key={key} content={component.content} />
            break
        case ArticleSectionType.RELATED_CONTEXT_ITEMS:
            pageItem = <ArticleRelatedContextItems key={key} {...component} />
            break
        case ArticleSectionType.TABLE:
            pageItem = <Table key={key} content={component} />
            break
        case ArticleSectionType.TEXT:
            pageItem = <ArticleText key={key} followLinks={followLinks} content={component} />
            break
        case ArticleSectionType.TOP_IMAGE:
            pageItem = <TopImage key={key} content={component} />
            break
        case ArticleSectionType.TOP_VIDEO:
            pageItem = <TopVideo key={key} content={component} />
            break
        case ArticleSectionType.VIDEO:
            pageItem = <ArticleVideo key={key} content={component} />
            break
    }
    return pageItem
}

export default renderBaseArticleComponent
